import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DemoBanner from '../components/DemoBanner';
import {
  Table, Alert, Container, Row, Col, Form, Button, Accordion, Modal, Badge
} from 'react-bootstrap';
import BrandImage from '../components/BrandImage';
import { useAuth } from '../context/AuthContext';
import { DICT } from '../utils/dictionary';
import { API_BASE, getAuthHeaders } from '../utils/api'; // Nova importação da infraestrutura

// --- MOCK DATA PROFISSIONAL (Modo Demonstração) ---
const getMockData = () => [
  {
    usuario_id: 1,
    usuario: `Carlos Silva (${DICT.CARGOS_ETAPAS[0] || 'Especialista'})`,
    total: 350.00,
    bonus: [
      { id: 101, video_id: 'abc123xyz', video_titulo: 'Estratégias de Crescimento 2026', valor: 200.00, data_postagem: '01/03/2026', data_bonus: '2026-03-02', views: 1250000, status: 'Pendente', data_confirmacao: null },
      { id: 102, video_id: 'def456uvw', video_titulo: 'Análise de Mercado SaaS', valor: 150.00, data_postagem: '28/02/2026', data_bonus: '2026-03-01', views: 800000, status: 'Pendente', data_confirmacao: null }
    ]
  },
  {
    usuario_id: 2,
    usuario: `Ana Souza (${DICT.CARGOS_ETAPAS[1] || 'Design'})`,
    total: 0.00,
    bonus: [
      { id: 103, video_id: 'ghi789rst', video_titulo: 'Nova Identidade Visual', valor: 50.00, data_postagem: '25/02/2026', data_bonus: '2026-02-28', views: 550000, status: 'Pago', data_confirmacao: new Date().toLocaleDateString('pt-BR').split('/').reverse().join('-') }
    ]
  }
];

const Pagamentos = () => {
  const { user, logout, isDemo, token } = useAuth();
  const navigate = useNavigate();

  const [bonusData, setBonusData] = useState([]);
  const [demoState, setDemoState] = useState(getMockData());

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('Todos');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalAction, setModalAction] = useState(null);

  const fetchBonusData = useCallback(() => {
    // --- LÓGICA DE DEMONSTRAÇÃO ---
    if (isDemo) {
      const filteredDemo = demoState.map(grupo => {
        const filteredBonus = grupo.bonus.filter(b => statusFilter === 'Todos' || b.status === statusFilter);
        const novoTotal = filteredBonus.filter(b => b.status === 'Pendente').reduce((acc, b) => acc + b.valor, 0);
        return { ...grupo, bonus: filteredBonus, total: novoTotal };
      }).filter(grupo => grupo.bonus.length > 0);

      setBonusData(filteredDemo);
      return;
    }

    // --- LÓGICA DE PRODUÇÃO ---
    const url = `${API_BASE}/folha_pagamento?data_inicio=${startDate}&data_fim=${endDate}&status=${statusFilter}`;
    fetch(url, { headers: getAuthHeaders(token) })
      .then(res => {
        if (res.status === 401) {
          logout();
          navigate('/login');
          return Promise.reject(new Error('Sessão expirada'));
        }
        return res.json();
      })
      .then(data => {
        if (data.message && !Array.isArray(data)) throw new Error(data.message);
        setBonusData(Array.isArray(data) ? data : []);
      })
      .catch(err => setError('Erro ao carregar dados: ' + err.message));
  }, [startDate, endDate, statusFilter, logout, navigate, isDemo, demoState, token]);

  useEffect(() => {
    if (!user || user.acesso === 'colaborador') {
      setError('Acesso restrito.');
      return;
    }
    fetchBonusData();
  }, [fetchBonusData, user, startDate, endDate, statusFilter]);

  const handleUpdateBonusStatus = async (bonusId, newStatus) => {
    setError(''); setSuccess('');

    if (isDemo) {
      setDemoState(prev => prev.map(grupo => {
        const updatedBonus = grupo.bonus.map(b => b.id === bonusId ? { ...b, status: newStatus } : b);
        const newTotal = updatedBonus.filter(b => b.status === 'Pendente').reduce((sum, b) => sum + b.valor, 0);
        return { ...grupo, bonus: updatedBonus, total: newTotal };
      }));
      setSuccess(`[DEMO] Lançamento #${bonusId} atualizado para ${newStatus}.`);
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/folha_pagamento/bonus/${bonusId}/status`, {
        method: 'PATCH',
        headers: getAuthHeaders(token),
        body: JSON.stringify({ status: newStatus })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Erro ao atualizar status.');
      setSuccess(`Lançamento #${bonusId} foi atualizado para ${newStatus}.`);
      fetchBonusData();
    } catch (err) {
      setError(`Erro: ${err.message}`);
    }
  };

  const handleFecharFolha = async () => {
    setError(''); setSuccess('');

    if (isDemo) {
      setSuccess('[DEMO] Folha de pagamento fechada com sucesso! Lançamentos registrados.');
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/folha_pagamento/fechar`, {
        method: 'POST',
        headers: getAuthHeaders(token)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setSuccess(data.message);
      fetchBonusData();
    } catch (err) {
      setError('Erro: ' + err.message);
    }
  };

  const openConfirmModal = (action) => {
    setModalAction(() => action);
    setShowConfirmModal(true);
  };

  const handleConfirmAction = () => {
    if (modalAction) modalAction();
    setShowConfirmModal(false);
    setModalAction(null);
  };

  const handleExportCSV = () => {
    const hoje = new Date();
    const dataBR = hoje.toLocaleDateString('pt-BR').split('/').reverse().join('-');
    const escapeCsvField = (field) => {
      if (field === null || field === undefined) return '';
      const str = String(field);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return '"' + str.replace(/"/g, '""') + '"';
      }
      return str;
    };
    const rows = [['Usuário', DICT.CONTENT_SING, 'Valor', 'Data Entrega', 'Data Pagamento', DICT.METRIC_PLUR, 'Status']];
    bonusData.forEach(grupo => {
      grupo.bonus.forEach(b => {
        if (b.status === 'Pago' && (isDemo || b.data_confirmacao === dataBR)) {
          rows.push([
            escapeCsvField(grupo.usuario),
            escapeCsvField(b.video_titulo),
            escapeCsvField(b.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })),
            escapeCsvField(b.data_postagem),
            escapeCsvField(b.data_confirmacao || '-'),
            escapeCsvField(b.views.toLocaleString('pt-BR')),
            escapeCsvField(b.status)
          ]);
        }
      });
    });
    if (rows.length === 1) {
      setError('Nenhum lançamento com status "Pago" encontrado para hoje.');
      return;
    }
    const csvContent = 'data:text/csv;charset=utf-8,' + rows.map(row => row.map(escapeCsvField).join(',')).join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `relatorio_pagamentos_${dataBR}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportPeriodoCSV = async () => {
    if (!startDate || !endDate) {
      setError('Informe a data inicial e final.');
      return;
    }

    if (isDemo) {
      setError('A geração de relatórios consolidados está desativada no modo demonstração.');
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/folha_pagamento/relatorio_periodo?data_inicio=${startDate}&data_fim=${endDate}`, {
        headers: getAuthHeaders(token)
      });
      if (!response.ok) throw new Error('Erro ao gerar relatório.');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `relatorio_pagamentos_${startDate}_a_${endDate}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (error) {
      setError('Erro: ' + error.message);
    }
  };

  return (
    <Container fluid className="main-content">
      {isDemo && <DemoBanner />}
      <Row className="align-items-center mb-4">
        <Col xs={12} md={6}>
          <h2 style={{ color: 'var(--brand-secondary)' }}>
            <i className="bi bi-cash-coin me-2" style={{ color: 'var(--brand-primary)' }}></i> Pagamentos e Folha
          </h2>
        </Col>
        <Col xs={12} md={6} className="d-none d-md-block text-end">
          <BrandImage 
            envKey="REACT_APP_LOGO_ICON" 
            defaultAsset="/client_assets/logo-icon.png" 
            alt="Ícone Pagamentos"
            style={{ maxWidth: 80, height: 'auto' }} 
          />
        </Col>
      </Row>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

      <div className="card border-0 shadow-sm mb-4 p-3">
        <Row className="align-items-end g-3">
          <Col md={3} sm={6}>
            <Form.Label className="small text-muted fw-bold">Data Inicial</Form.Label>
            <Form.Control type="date" value={startDate} onChange={e => setStartDate(e.target.value)} size="sm" />
          </Col>
          <Col md={3} sm={6}>
            <Form.Label className="small text-muted fw-bold">Data Final</Form.Label>
            <Form.Control type="date" value={endDate} onChange={e => setEndDate(e.target.value)} size="sm" />
          </Col>
          <Col md={3} sm={12}>
            <Form.Label className="small text-muted fw-bold">Status</Form.Label>
            <Form.Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} size="sm">
              <option value="Pendente">Pendente</option>
              <option value="Pago">Pago</option>
              <option value="Todos">Todos</option>
            </Form.Select>
          </Col>
        </Row>

        <Row className="mt-4">
          <Col className="d-flex flex-wrap gap-2">
            <Button variant="outline-primary" size="sm" onClick={handleExportCSV}>
              <i className="bi bi-download me-1"></i> Exportar Lote Diário
            </Button>
            <Button variant="outline-secondary" size="sm" onClick={handleExportPeriodoCSV}>
              <i className="bi bi-calendar-range-fill me-1"></i> Relatório por Período
            </Button>
            {user?.acesso === 'admin' && (
              <Button variant="danger" size="sm" className="ms-auto" onClick={() => openConfirmModal(handleFecharFolha)}>
                <i className="bi bi-lock-fill me-1"></i> Fechar Folha
              </Button>
            )}
          </Col>
        </Row>
      </div>

      {bonusData.length === 0 ? (
        <Alert variant="info" className="border-0 shadow-sm">Nenhum lançamento encontrado para os filtros selecionados.</Alert>
      ) : (
        <Accordion alwaysOpen className="shadow-sm">
          {bonusData.map((grupo, index) => (
            <Accordion.Item eventKey={String(index)} key={grupo.usuario_id} className="border-0 border-bottom">
              <Accordion.Header>
                <div className="d-flex justify-content-between w-100 pe-3 align-items-center">
                  <span className="fw-bold" style={{ color: 'var(--brand-secondary)' }}>{grupo.usuario}</span>
                  <span className="text-muted small">
                    Pendente: <strong className="fs-6" style={{ color: 'var(--brand-primary)' }}>{grupo.total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</strong>
                  </span>
                </div>
              </Accordion.Header>
              <Accordion.Body className="p-0">
                <Table striped hover responsive className="mb-0 align-middle">
                  <thead className="bg-light text-muted small text-uppercase">
                    <tr>
                      <th className="border-0 ps-4">{DICT.CONTENT_SING}</th>
                      <th className="border-0">Valor</th>
                      <th className="border-0">Entrega</th>
                      <th className="border-0">Data do Lançamento</th>
                      <th className="border-0">{DICT.METRIC_PLUR}</th>
                      <th className="border-0">Status</th>
                      {(user.acesso === 'admin' || user.acesso === 'gerente') && <th className="border-0 text-end pe-4">Ações</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {grupo.bonus.map(b => (
                      <tr key={b.id}>
                        <td className="ps-4">
                          <a
                            href={b.video_url || (b.video_id ? `https://www.youtube.com/watch?v=${b.video_id}` : '#')}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: 'var(--brand-primary)', textDecoration: 'none', fontWeight: '500' }}
                          >
                            {b.video_titulo} <i className="bi bi-box-arrow-up-right ms-1 small text-muted"></i>
                          </a>
                        </td>
                        <td className="fw-bold">{b.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' } )}</td>
                        <td>{b.data_postagem}</td>
                        <td>{new Date(b.data_bonus).toLocaleDateString('pt-BR')}</td>
                        <td>{b.views.toLocaleString('pt-BR')}</td>
                        <td>
                          <Badge bg={b.status === 'Pago' ? 'success' : 'warning'} className="text-uppercase" style={{ fontSize: 'var(--font-size-xs)' }}>
                            {b.status}
                          </Badge>
                        </td>
                        {(user.acesso === 'admin' || user.acesso === 'gerente') && (
                          <td className="text-end pe-4">
                            {b.status === 'Pendente' ? (
                              <Button variant="success" size="sm" onClick={() => openConfirmModal(() => handleUpdateBonusStatus(b.id, 'Pago'))} className="border-0">
                                <i className="bi bi-check-circle-fill"></i> Pagar
                              </Button>
                            ) : (
                              <Button variant="outline-warning" size="sm" onClick={() => openConfirmModal(() => handleUpdateBonusStatus(b.id, 'Pendente'))} className="border-0">
                                <i className="bi bi-arrow-counterclockwise"></i> Reabrir
                              </Button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      )}

      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered size="sm">
        <Modal.Body className="text-center p-4">
          <h5 className="fw-bold mb-3">Confirmação</h5>
          <p className="text-muted small mb-4">Você tem certeza que deseja executar esta ação? Ela pode alterar relatórios financeiros.</p>
          <div className="d-grid gap-2">
            <Button variant="primary" onClick={handleConfirmAction} style={{ backgroundColor: 'var(--brand-primary)', borderColor: 'var(--brand-primary)' }}>Confirmar Ação</Button>
            <Button variant="light" onClick={() => setShowConfirmModal(false)} className="text-muted border">Cancelar</Button>
          </div>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default Pagamentos;